import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import { useFormikContext } from "formik";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import Input from "../../../../../Components/Common/Input/Input";
import PhoneInput from "../../../../../Components/Common/PhoneInput/PhoneInput";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import { PreconFormValues } from "../../types/precon.types";
import { CHAR_LIMIT, CHAR_MIN } from "../../../../../utils/Common/Constant";

interface Props {
  prefilledFields: Set<string>;
}

const Step2Certification = (_: Props) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<PreconFormValues>();

  const err = (field: keyof PreconFormValues) =>
    errors[field] as string | undefined;
  const isTouched = (field: keyof PreconFormValues) =>
    touched[field] as boolean | undefined;

  const hasErr = (
    field: keyof PreconFormValues,
    have?: number,
    min?: number,
  ): boolean => {
    const val = String(values[field] ?? "");
    const len = val.length;
    const isPhoneEmpty =
      val.startsWith("+") && val.replace(/\D/g, "").length <= 3;
    if (have !== undefined && len > have) return true;
    if (min !== undefined && !isPhoneEmpty && len > 0 && len < min) return true;
    return !!(isTouched(field) && err(field));
  };

  return (
    <div className="screen active">
      <SectionHeader
        title="Certification"
        subtitle="The standard CSC certification language — edit as needed for this specific engagement."
      />

      <Card title="Certification Statement" badge="Editable" badgeType="edit">
        <div className="field fieldInline">
          <label>Opening Paragraph</label>
          <TextArea
            name="certificationOpening"
            placeholder="Enter the opening paragraph for the certification statement..."
            className={`ft textareaTall${hasErr("certificationOpening") ? " error-input-red" : ""}`}
            value={values.certificationOpening}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            touched={isTouched("certificationOpening")}
            error={err("certificationOpening")}
          />
        </div>

        <div className="field fieldInline">
          <label>Report Standards Statement</label>
          <TextArea
            name="certificationStandards"
            placeholder="Enter the report standards statement for the certification statement..."
            className={`ft textareaTall${hasErr("certificationStandards") ? " error-input-red" : ""}`}
            value={values.certificationStandards}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            touched={isTouched("certificationStandards")}
            error={err("certificationStandards")}
          />
        </div>

        <div className="field fieldInline">
          <label>Basis of Findings</label>
          <TextArea
            name="certificationFindings"
            placeholder="Enter the basis of findings for the certification statement..."
            className={`ft textareaTall${hasErr("certificationFindings") ? " error-input-red" : ""}`}
            value={values.certificationFindings}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            touched={isTouched("certificationFindings")}
            error={err("certificationFindings")}
          />
        </div>

        <div className="fieldRow c2">
          <div className="field">
            <label>Prepared and Approved By</label>
            <Input
              name="certificationPreparedBy"
              placeholder="Name"
              className={`fi${hasErr("certificationPreparedBy", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.certificationPreparedBy}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
            />
            <ErrorMessage
              value={values.certificationPreparedBy}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("certificationPreparedBy")}
              error={err("certificationPreparedBy")}
            />
          </div>
          <div className="field">
            <label>Organization</label>
            <Input
              name="certificationOrganization"
              placeholder="Organization"
              className={`fi${hasErr("certificationOrganization", CHAR_LIMIT.NAME) ? " error-input-red" : ""}`}
              value={values.certificationOrganization}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.NAME}
            />
            <ErrorMessage
              value={values.certificationOrganization}
              have={CHAR_LIMIT.NAME}
              touched={isTouched("certificationOrganization")}
              error={err("certificationOrganization")}
            />
          </div>
        </div>

        <div className="fieldRow c2">
          <div className="field">
            <label>Contact Phone</label>
            <PhoneInput
              value={values.certificationPhone}
              onChange={(value) => setFieldValue("certificationPhone", value)}
              onBlur={() =>
                handleBlur({ target: { name: "certificationPhone" } } as any)
              }
              error={hasErr(
                "certificationPhone",
                CHAR_LIMIT.PHONE,
                CHAR_MIN.CONTACT,
              )}
            />
            <ErrorMessage
              value={values.certificationPhone}
              min={CHAR_MIN.CONTACT}
              have={CHAR_LIMIT.PHONE}
              touched={isTouched("certificationPhone")}
              error={err("certificationPhone")}
            />
          </div>
          <div className="field">
            <label>Contact Email</label>
            <Input
              name="certificationEmail"
              placeholder="email@123.com"
              className={`fi${hasErr("certificationEmail", CHAR_LIMIT.EMAIL) ? " error-input-red" : ""}`}
              type="email"
              value={values.certificationEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.EMAIL}
            />
            <ErrorMessage
              value={values.certificationEmail}
              have={CHAR_LIMIT.EMAIL}
              touched={isTouched("certificationEmail")}
              error={err("certificationEmail")}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Step2Certification;
